import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  activateDismantlingMethod,
  createDismantlingMethod,
  deactivateDismantlingMethod,
  listDismantlingMethods,
  purgeDismantlingMethod,
  updateDismantlingMethod,
  type DismantlingMethodListParams,
} from './api';
import type {
  DismantlingMethodCreateInput,
  DismantlingMethodMaster,
  DismantlingMethodUpdateInput,
} from './types';

const KEY = 'dismantling-methods' as const;

export const useDismantlingMethodsList = (params: DismantlingMethodListParams = {}) =>
  useQuery<DismantlingMethodMaster[], ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listDismantlingMethods(params),
    placeholderData: (prev) => prev,
  });

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [KEY] });
};

export const useCreateDismantlingMethod = () => {
  const invalidate = useInvalidate();
  return useMutation<DismantlingMethodMaster, ApiClientError, DismantlingMethodCreateInput>({
    mutationFn: (body) => createDismantlingMethod(body),
    onSuccess: invalidate,
  });
};

export const useUpdateDismantlingMethod = () => {
  const invalidate = useInvalidate();
  return useMutation<
    DismantlingMethodMaster,
    ApiClientError,
    { id: string; body: DismantlingMethodUpdateInput }
  >({
    mutationFn: ({ id, body }) => updateDismantlingMethod(id, body),
    onSuccess: invalidate,
  });
};

export const useDeactivateDismantlingMethod = () => {
  const invalidate = useInvalidate();
  return useMutation<DismantlingMethodMaster, ApiClientError, string>({
    mutationFn: (id) => deactivateDismantlingMethod(id),
    onSuccess: invalidate,
  });
};

export const useActivateDismantlingMethod = () => {
  const invalidate = useInvalidate();
  return useMutation<DismantlingMethodMaster, ApiClientError, string>({
    mutationFn: (id) => activateDismantlingMethod(id),
    onSuccess: invalidate,
  });
};

export const usePurgeDismantlingMethod = () => {
  const invalidate = useInvalidate();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeDismantlingMethod(id),
    onSuccess: invalidate,
  });
};
